import React, {
  useState, useEffect, useMemo, useRef,
} from 'react';
import { useFetch } from '../api';
import {
  SANDBOX_TYPES, RESOURCES_BY_TYPE, canCreate,
} from './sandboxConfig';
import {
  getResources, generateSandbox, getSandboxWriteInfo, simulateCreateCartResource,
} from './SandboxApi';
import SandboxTypeTabs from './SandboxTypeTabs';
import ResourceListSidebar from './ResourceListSidebar';
import JsonResponsePanel from './JsonResponsePanel';
import ResourceEditorModal from './ResourceEditorModal';
import GenerateSandboxModal from './GenerateSandboxModal';
import ReceiveReturnModal from './ReceiveReturnModal';

function SandboxPage({ connectionData, fetchConnectionData }) {
  const trackstar = useFetch();

  const [sandboxType, setSandboxType] = useState('wms');
  const [selectedResource, setSelectedResource] = useState('orders');
  const [rows, setRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);

  const [resourceCounts, setResourceCounts] = useState({});
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // modals
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorResource, setEditorResource] = useState(null);
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [newLoading, setNewLoading] = useState(false);
  const [receiveReturnOpen, setReceiveReturnOpen] = useState(false);
  const [receiveReturnData, setReceiveReturnData] = useState(null);
  const [error, setError] = useState(null);
  const [sandboxSpec, setSandboxSpec] = useState(null);

  // derive sandbox connections from App's connectionData
  const sandboxConnections = useMemo(() => {
    if (!connectionData) return {};
    const map = {};
    SANDBOX_TYPES.forEach(({ key }) => {
      const conn = connectionData.find(
        (c) => c.integration_name === 'sandbox' && c.integration_type === key,
      );
      if (conn) map[key] = conn.connection_id;
    });
    return map;
  }, [connectionData]);

  const connectionId = sandboxConnections[sandboxType];
  const currentResourceConfig = (RESOURCES_BY_TYPE[sandboxType] || [])
    .find((r) => r.key === selectedResource);

  // track the live selection so an in-flight create can tell if the user navigated away
  const contextRef = useRef({ sandboxType, selectedResource });
  contextRef.current = { sandboxType, selectedResource };

  const sidebarLoading = !connectionData;

  // fetch wms sandbox write schema once for form defaults
  useEffect(() => {
    if (!connectionId || sandboxSpec) return;
    getSandboxWriteInfo(trackstar, connectionId).then(setSandboxSpec);
  }, [connectionId]);

  // clear selection when switching resource or connection
  useEffect(() => {
    setSelectedRow(null);
  }, [connectionId, selectedResource]);

  // fetch resources
  useEffect(() => {
    if (!connectionId || !currentResourceConfig) {
      setRows([]);
      setResourcesLoading(false);
      return;
    }
    setResourcesLoading(true);
    let stale = false;
    getResources(trackstar, connectionId, currentResourceConfig.apiPath).then((data) => {
      if (stale) return;
      setRows(data);
      setResourceCounts((prev) => ({ ...prev, [selectedResource]: data.length }));
      setResourcesLoading(false);
    });
    // eslint-disable-next-line consistent-return
    return () => { stale = true; };
  }, [connectionId, currentResourceConfig?.apiPath, selectedResource, refreshKey]);

  // fetch counts for all resources once the sandbox connection is known
  useEffect(() => {
    if (!connectionId) return;
    const resources = RESOURCES_BY_TYPE[sandboxType] || [];
    resources.forEach((r) => {
      getResources(trackstar, connectionId, r.apiPath).then((data) => {
        setResourceCounts((prev) => ({ ...prev, [r.key]: data.length }));
      });
    });
  }, [connectionId, sandboxType]);

  // reset selected resource when switching types
  useEffect(() => {
    const resources = [...RESOURCES_BY_TYPE[sandboxType]]
      .sort((a, b) => a.label.localeCompare(b.label));
    setSelectedResource(resources[0].key);
    setSelectedRow(null);
    setResourceCounts({});
  }, [sandboxType]);

  const handleGenerate = () => {
    setGenerateLoading(true);
    generateSandbox(trackstar, sandboxType).then(() => {
      fetchConnectionData();
      setGenerateLoading(false);
      setGenerateModalOpen(false);
    });
  };

  const handleUpdateClick = (row) => {
    setEditorResource(row);
    setEditorOpen(true);
  };

  const handleNewClick = () => {
    // cart auto-generates the order/product server-side, no editor modal
    if (sandboxType === 'cart') {
      const createdFor = { sandboxType, selectedResource };
      setNewLoading(true);
      simulateCreateCartResource(trackstar, connectionId, selectedResource)
        .then((created) => {
          // drop the result if the user switched tab or resource while it was in flight
          const now = contextRef.current;
          if (now.sandboxType !== createdFor.sandboxType
            || now.selectedResource !== createdFor.selectedResource) return;
          setRefreshKey((k) => k + 1);
          // open the data explorer to the item we just made
          if (created) setSelectedRow(created);
        })
        .catch((e) => {
          // same guard: don't surface a stale failure in a context the user left
          const now = contextRef.current;
          if (now.sandboxType !== createdFor.sandboxType
            || now.selectedResource !== createdFor.selectedResource) return;
          setError(String(e));
        })
        .finally(() => setNewLoading(false));
      return;
    }
    setEditorResource(null);
    setEditorOpen(true);
  };

  const handleEditorSuccess = () => {
    setEditorOpen(false);
    setEditorResource(null);
    setRefreshKey((k) => k + 1);
  };

  const handleRefresh = () => {
    setRefreshKey((k) => k + 1);
  };

  const handleModalAction = (modalType, row) => {
    if (modalType === 'receiveReturn') {
      setReceiveReturnData(row);
      setReceiveReturnOpen(true);
    }
  };

  // keep selectedRow in sync with refreshed rows
  useEffect(() => {
    if (!selectedRow) return;
    const updated = rows.find((r) => r.id === selectedRow.id);
    if (updated) setSelectedRow(updated);
  }, [rows]);

  return (
    <>
      {/* tabs */}
      <SandboxTypeTabs
        activeType={sandboxType}
        onChange={setSandboxType}
        onRegenerateClick={() => setGenerateModalOpen(true)}
      />

      {/* sidebar + response panel */}
      <div className="flex-1 min-h-0 card overflow-hidden flex">
        <ResourceListSidebar
          sandboxType={sandboxType}
          selectedResource={selectedResource}
          onResourceChange={setSelectedResource}
          rows={rows}
          resourceCounts={resourceCounts}
          selectedRowId={selectedRow?.id}
          onRowSelect={(row) => setSelectedRow(row || null)}
          onNewClick={handleNewClick}
          onGenerateClick={() => setGenerateModalOpen(true)}
          hasConnection={!!connectionId}
          loading={sidebarLoading}
          resourcesLoading={resourcesLoading}
          newLoading={newLoading}
        />
        <JsonResponsePanel
          row={selectedRow}
          sandboxType={sandboxType}
          resourceKey={selectedResource}
          apiPath={currentResourceConfig?.apiPath}
          connectionId={connectionId}
          onRefresh={handleRefresh}
          onUpdateClick={handleUpdateClick}
          onModalAction={handleModalAction}
        />
      </div>

      {/* modals */}
      {canCreate(sandboxType) && (
        <ResourceEditorModal
          isOpen={editorOpen}
          onClose={() => { setEditorOpen(false); setEditorResource(null); }}
          connectionId={connectionId}
          sandboxType={sandboxType}
          resourceKey={selectedResource}
          apiPath={currentResourceConfig?.apiPath}
          existingResource={editorResource}
          onSuccess={handleEditorSuccess}
          onError={setError}
          openApiSpec={sandboxSpec}
        />
      )}

      <ReceiveReturnModal
        isOpen={receiveReturnOpen}
        onClose={() => { setReceiveReturnOpen(false); setReceiveReturnData(null); }}
        returnData={receiveReturnData}
        connectionId={connectionId}
        onSuccess={() => {
          setReceiveReturnOpen(false);
          setReceiveReturnData(null);
          handleRefresh();
        }}
        onError={setError}
      />

      <GenerateSandboxModal
        isOpen={generateModalOpen}
        onClose={() => setGenerateModalOpen(false)}
        onConfirm={handleGenerate}
        loading={generateLoading}
        sandboxType={sandboxType}
      />

      {error && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="modal-backdrop" onClick={() => setError(null)} aria-hidden="true" />
          <div className="modal max-w-sm">
            <div className="modal-header">
              <h2 className="text-lg font-semibold text-surface-900">Error</h2>
            </div>
            <div className="modal-body">
              <p className="text-sm text-surface-600 whitespace-pre-wrap">{error}</p>
            </div>
            <div className="modal-footer">
              <button type="button" onClick={() => setError(null)} className="btn btn-secondary">Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SandboxPage;
